import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
  Font,
} from "@react-pdf/renderer";
import { getErpBranchDetails, getTenantBrandImageSrcWithFallback } from "../erpClient";
import { capitalizeWords } from "../utils/capitalizeWords";
import { getSessionBranchId } from "../utils/sessionBranch";
import { getTenantNameUpper } from "../utils/tenantName";

Font.register({
  family: "NotoSansGujaratiUI",
  src: "/assets/fonts/gujarati/NotoSansGujaratiUI-Regular.ttf",
});

Font.register({
  family: "NotoSansGujaratiUI",
  src: "/assets/fonts/gujarati/NotoSansGujaratiUI-Bold.ttf",
  fontWeight: "bold",
});

// A5 landscape — 8mm gap between page edge and content border (all sides)
const PAGE_MARGIN = 22.68; // 8mm in pt (8 * 72 / 25.4)
const A5_LANDSCAPE = [595.28, 419.53]; // [width, height]
const CONTENT_HEIGHT = A5_LANDSCAPE[1] - PAGE_MARGIN * 2;

function formatBranchAddressLines(address) {
  const raw = String(address || "").trim();
  if (!raw) return ["-"];
  if (raw.includes("\n")) {
    return raw
      .split(/\n/)
      .map((s) => s.trim())
      .filter(Boolean);
  }
  const parts = raw
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  if (parts.length <= 1) return [raw];
  if (parts.length === 2) return [`${parts[0]},`, parts[1]];
  return [`${parts.slice(0, 2).join(", ")},`, parts.slice(2).join(", ")];
}

const styles = StyleSheet.create({
  page: {
    padding: PAGE_MARGIN,
    fontFamily: "Helvetica",
    backgroundColor: "#fff",
    flexDirection: "column",
  },

  container: {
    flex: 1,
    height: CONTENT_HEIGHT,
    maxHeight: CONTENT_HEIGHT,
    border: "1pt solid #8c1d1a",
    borderRadius: 6,
    padding: 0,
    flexDirection: "column",
  },
  topHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingBottom: 2,
    marginBottom: 2,
    paddingTop: 2,
    borderBottom: "1pt solid #8c1d1a",
    paddingLeft: 10,
    paddingRight: 10,
    flexShrink: 0,
  },
  topHeaderText: {
    fontSize: 8,
    fontWeight: "bold",
    fontFamily: "NotoSansGujaratiUI",
    color: "#8c1d1a",
  },
  topHeaderLeft: {
    width: "34%",
    alignItems: "flex-start",
  },
  topHeaderCenter: {
    width: "32%",
    alignItems: "center",
  },
  topHeaderRight: {
    width: "34%",
    alignItems: "flex-end",
  },
  header: {
    position: "relative",
    paddingTop: 4,
    paddingBottom: 5,
    paddingLeft: 8,
    paddingRight: 8,
    borderBottom: "1pt solid #8c1d1a",
    minHeight: 44,
    flexShrink: 0,
  },
  logoCol: {
    position: "absolute",
    left: 8,
    top: 6,
    width: 36,
    height: 36,
    zIndex: 2,
  },
  centerBrandBlock: {
    width: "100%",
    alignItems: "center",
    paddingHorizontal: 70,
  },
  companyTitle: {
    fontFamily: "Times-Bold",
    fontSize: 22,
    color: "#7d0000",
    letterSpacing: 0.4,
    textAlign: "center",
  },
  companyAddress: {
    fontSize: 7,
    marginTop: 2,
    textAlign: "center",
    width: "100%",
  },
  mobileCol: {
    position: "absolute",
    right: 8,
    top: 6,
    zIndex: 2,
  },
  mobileText: {
    fontSize: 8,
    fontWeight: "bold",
    textAlign: "right",
    fontFamily: "NotoSansGujaratiUI",
  },
  titleSection: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    borderBottom: "1pt solid #8c1d1a",
    paddingBottom: 0,
    marginBottom: 0,
    backgroundColor: "#8c1d1a",
    marginLeft: 0,
    marginRight: 0,
    paddingLeft: 0,
    paddingRight: 0,
    flexShrink: 0,
  },
  titleText: {
    fontSize: 13,
    fontWeight: "medium",
    color: "white",
  },

  customerSection: {
    borderBottom: "1pt solid #8c1d1a",
    height: 42,
    flexShrink: 0,
  },
  singleLineField: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 10,
    height: 21,
  },
  singleLineFieldBottom: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 10,
    height: 21,
  },
  customerMobileWrap: {
    flexDirection: "row",
    alignItems: "center",
    marginLeft: 10,
    minWidth: 150,
  },
  customerMobileValueLine: {
    flex: 1,
    borderBottom: "1pt solid #8c1d1a",
    marginLeft: 4,
    minHeight: 14,
    justifyContent: "flex-end",
  },
  fieldLabel: {
    fontSize: 10,
    fontWeight: "bold",
    fontFamily: "NotoSansGujaratiUI",
  },
  fieldValueLine: {
    flex: 1,
    borderBottom: "1pt solid #8c1d1a",
    marginLeft: 4,
    paddingBottom: 1,
    minHeight: 14,
    justifyContent: "flex-end",
  },
  fieldValueText: {
    fontSize: 9,
    fontWeight: "bold",
    paddingLeft: 4,
    fontFamily: "NotoSansGujaratiUI",
  },
  fieldValueTextSmall: {
    fontSize: 9,
    fontWeight: "bold",
    paddingLeft: 4,
    fontFamily: "NotoSansGujaratiUI",
  },

  detailTable: {
    flex: 1,
    minHeight: 120,
    flexDirection: "column",
  },
  tableHeader: {
    flexDirection: "row",
    borderBottom: "1pt solid #8c1d1a",
    backgroundColor: "#f5e9e9",
    height: 20,
    alignItems: "center",
    flexShrink: 0,
  },
  colDetail: {
    width: "50%",
    borderRight: "1pt solid #8c1d1a",
    paddingHorizontal: 4,
    justifyContent: "center",
    height: "100%",
  },
  colGrossWeight: {
    width: "15%",
    borderRight: "1pt solid #8c1d1a",
    paddingHorizontal: 4,
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
  },
  colWeight: {
    width: "20%",
    borderRight: "1pt solid #8c1d1a",
    paddingHorizontal: 4,
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
  },
  colAmount: {
    width: "15%",
    paddingHorizontal: 4,
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
  },
  th: {
    fontSize: 10,
    fontWeight: "bold",
    textAlign: "center",
    fontFamily: "NotoSansGujaratiUI",
  },
  bodyArea: {
    flex: 1,
    position: "relative",
  },
  bodyColumns: {
    position: "absolute",
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    flexDirection: "row",
  },
  detailColBody: {
    width: "50%",
    borderRight: "1pt solid #8c1d1a",
  },
  grossWeightColBody: {
    width: "15%",
    borderRight: "1pt solid #8c1d1a",
  },
  weightColBody: {
    width: "20%",
    borderRight: "1pt solid #8c1d1a",
  },
  amountColBody: {
    width: "15%",
  },
  watermarkDetailArea: {
    position: "absolute",
    left: 0,
    top: 0,
    bottom: 0,
    width: "50%",
    alignItems: "center",
    justifyContent: "center",
  },
  rowsLayer: {
    width: "100%",
  },
  itemRow: {
    flexDirection: "row",
    minHeight: 20,
    alignItems: "flex-start",
  },
  itemDetailCell: {
    width: "50%",
    borderRight: "1pt solid transparent",
    paddingLeft: 6,
    paddingRight: 4,
    paddingVertical: 2,
    justifyContent: "center",
    minHeight: 20,
  },
  itemDetail: {
    fontSize: 9,
    fontWeight: "bold",
    fontFamily: "NotoSansGujaratiUI",
  },
  itemGrossWeightCell: {
    width: "15%",
    paddingHorizontal: 2,
    paddingVertical: 2,
    alignItems: "center",
    justifyContent: "center",
    minHeight: 20,
  },
  itemGrossWeight: {
    textAlign: "center",
    fontSize: 9,
    fontWeight: "bold",
    fontFamily: "NotoSansGujaratiUI",
  },
  itemWeightCell: {
    width: "20%",
    paddingHorizontal: 2,
    paddingVertical: 2,
    alignItems: "center",
    justifyContent: "center",
    minHeight: 20,
  },
  itemWeight: {
    textAlign: "center",
    fontSize: 9,
    fontWeight: "bold",
    fontFamily: "NotoSansGujaratiUI",
  },
  itemAmountCell: {
    width: "15%",
    paddingHorizontal: 2,
    paddingVertical: 2,
    alignItems: "center",
    justifyContent: "center",
    minHeight: 20,
  },
  itemAmount: {
    textAlign: "center",
    fontSize: 9,
    fontWeight: "bold",
  },
  watermarkWrap: {
    opacity: 0.14,
  },
  watermark: {
    width: 86,
    height: 86,
  },

  totalRow: {
    flexDirection: "row",
    borderTop: "1pt solid #8c1d1a",
    height: 24,
    flexShrink: 0,
  },
  totalDetailCell: {
    width: "50%",
    borderRight: "1pt solid #8c1d1a",
    alignItems: "flex-start",
    justifyContent: "center",
    paddingHorizontal: 6,
  },
  totalGrossWeightCell: {
    width: "15%",
    borderRight: "1pt solid #8c1d1a",
  },
  totalLabelCell: {
    width: "20%",
    borderRight: "1pt solid #8c1d1a",
    alignItems: "center",
    justifyContent: "center",
  },
  noteText: {
    fontSize: 8,
    fontWeight: "bold",
    fontFamily: "NotoSansGujaratiUI",
  },
  totalAmountCell: {
    width: "15%",
    alignItems: "center",
    justifyContent: "center",
  },
  totalLabel: {
    fontSize: 10,
    fontWeight: "bold",
    fontFamily: "NotoSansGujaratiUI",
  },
  totalAmount: {
    fontSize: 10,
    fontWeight: "bold",
  },

  footer: {
    height: 45,
    borderTop: "1pt solid #8c1d1a",
    paddingHorizontal: 10,
    paddingTop: 3,
    paddingBottom: 5,
    flexDirection: "row",
    justifyContent: "space-between",
    flexShrink: 0,
  },
  terms: {
    width: "75%",
    fontWeight: "bold",
  },
  termsText: {
    fontSize: 8.8,
    lineHeight: 2,
    fontFamily: "NotoSansGujaratiUI",
  },
  signature: {
    width: "25%",
    justifyContent: "flex-end",
    alignItems: "flex-end",
    paddingBottom: 2,
  },
  signatureText: {
    fontSize: 12,
    color: "#8c1d1a",
    fontWeight: "bold",
  },

  // Additional styles for compatibility
  sellerSection: {
    marginBottom: 10,
  },

  sellerText: {
    fontSize: 8,
    marginBottom: 1,
    lineHeight: 1.4,
  },
});

const formatDate = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
};

const toNumber = (value) => {
  const n = parseFloat(value);
  return Number.isFinite(n) ? n : 0;
};

const cleanText = (value) => {
  const str = String(value ?? "").trim();
  if (!str) return "";
  const lowered = str.toLowerCase();
  if (
    lowered === "n/a" ||
    lowered === "na" ||
    lowered === "null" ||
    lowered === "undefined"
  ) {
    return "";
  }
  return str;
};

const toEnglishDigits = (value) =>
  String(value ?? "").replace(/[૦-૯]/g, (digit) =>
    String("૦૧૨૩૪૫૬૭૮૯".indexOf(digit)),
  );

const buildDetailText = (item = {}) => {
  return (
    item.item_category_name ||
    item.itemCategoryName ||
    item.name ||
    item.design ||
    ""
  );
};

const buildWeightText = (item = {}) => {
  const weight =
    item.currentWeight ?? item.grossWeight ?? item.current_weight ?? "";
  if (weight === null || weight === undefined || String(weight).trim() === "") {
    return "";
  }
  return String(weight);
};

const buildIssueText = (item = {}) => {
  return String(
    item.issueDescription ?? item.issue_description ?? item.description ?? "",
  ).trim();
};

const buildIssueNote = (items = []) => {
  if (!Array.isArray(items)) return "";
  const uniqueIssues = [];
  items.forEach((item) => {
    const issue = String(
      item?.issueDescription || item?.issue_description || "",
    ).trim();
    if (issue && !uniqueIssues.includes(issue)) {
      uniqueIssues.push(issue);
    }
  });
  return uniqueIssues.join(", ");
};

const buildPaymentNote = (payments = []) => {
  if (!Array.isArray(payments) || payments.length === 0) return "-";
  const entries = payments.map((payment) => {
    const paymentMode = payment?.bank_name || payment?.payment_mode || "-";
    const amountRaw = payment?.amount;
    const amount =
      amountRaw === null || amountRaw === undefined || amountRaw === ""
        ? "-"
        : toNumber(amountRaw).toFixed(2);
    return `${paymentMode} - ${amount}`;
  });
  return entries.join(", ");
};

const RepairingSlipPDF = ({ slipData }) => {
  const data = slipData || {};
  const customer = data.customer || {};
  const sessionBranchId = getSessionBranchId();
  const branchList = getErpBranchDetails();
  const branchDetails =
    branchList.find((b) => Number(b?.id) === Number(sessionBranchId)) ||
    branchList[0] ||
    {};
  const rows = Array.isArray(data.items) ? data.items.slice(0, 8) : [];
  const paddedRows = [...rows];
  while (paddedRows.length < 8) paddedRows.push(null);

  const totalAmount = toNumber(data.finalAmount);
  const noteText = buildPaymentNote(data.payments);
  const companyNameUpper = getTenantNameUpper();
  const companyMobile = toEnglishDigits(
    branchDetails.mobile || branchDetails.phone || "-",
  );
  const signatureCompanyStyle =
    companyNameUpper.length > 22
      ? [styles.signatureText, { fontSize: 8 }]
      : companyNameUpper.length > 16
        ? [styles.signatureText, { fontSize: 9 }]
        : styles.signatureText;
  const addressLines = formatBranchAddressLines(branchDetails.address);
  const addressSingleLine =
    String(branchDetails.address || "")
      .replace(/\s*\n\s*/g, ", ")
      .replace(/,\s*,/g, ",")
      .replace(/\s+/g, " ")
      .trim() ||
    addressLines.join(", ").replace(/\s+/g, " ").trim() ||
    "-";
  const estimatedDelivery =
    data.expectedDelivery ||
    data.exp_delivery ||
    data.expected_delivery ||
    data.delivery_date ||
    "";

  return (
    <Document>
      <Page size={A5_LANDSCAPE} style={styles.page} wrap={false}>
        <View style={styles.container}>
          <View style={styles.topHeader}>
            <View style={styles.topHeaderLeft}>
              <Text style={styles.topHeaderText}>
                || શ્રી પદમાવતી માતાય નમ: ||
              </Text>
            </View>
            <View style={styles.topHeaderCenter}>
              <Text style={styles.topHeaderText}>|| શ્રી ગણેશાય નમ: ||</Text>
            </View>
            <View style={styles.topHeaderRight}>
              <Text style={styles.topHeaderText}>
                || શ્રી બુટભવાની માતાય નમ: ||
              </Text>
            </View>
          </View>

          <View style={styles.header}>
            <View style={styles.mobileCol}>
              <Text style={styles.mobileText} wrap={false}>
                MO.: {companyMobile}
              </Text>
            </View>
            <View style={styles.logoCol}>
              <Image
                src={getTenantBrandImageSrcWithFallback("sidebar_icon")}
                style={{ height: 32, width: 32 }}
              />
            </View>
            <View style={styles.centerBrandBlock}>
              <Text style={styles.companyTitle} wrap={false}>
                {companyNameUpper}
              </Text>
              <Text style={styles.companyAddress} wrap={false}>
                {addressSingleLine}
              </Text>
            </View>
          </View>

          <View style={styles.titleSection}>
            <View style={{ flex: 1, alignItems: "center" }}>
              <Text style={styles.titleText}>Repairing Order</Text>
            </View>
          </View>

          <View
            style={{
              marginTop: 2,
              paddingLeft: 10,
              paddingRight: 10,
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "flex-start",
              flexShrink: 0,
            }}
          >
            <View>
              <Text
                style={{
                  fontSize: 8,
                  fontWeight: "bold",
                  marginBottom: 1,
                  fontFamily: "NotoSansGujaratiUI",
                }}
              >
                Dt. : {formatDate(data.date)}
              </Text>
            </View>
            <View>
              <Text
                style={{
                  fontSize: 8,
                  fontWeight: "bold",
                  marginBottom: 1,
                  fontFamily: "NotoSansGujaratiUI",
                }}
              >
                Est. Delivery : {formatDate(estimatedDelivery)}
              </Text>
            </View>
          </View>

          <View style={styles.customerSection}>
            <View style={styles.singleLineField}>
              <Text style={styles.fieldLabel}>To:</Text>
              <View style={styles.fieldValueLine}>
                <Text style={styles.fieldValueText}>
                  {capitalizeWords(cleanText(customer.name))}
                </Text>
              </View>
              <View style={styles.customerMobileWrap}>
                <Text style={styles.fieldLabel}>Mo.:</Text>
                <View style={styles.customerMobileValueLine}>
                  <Text style={styles.fieldValueTextSmall}>
                    {cleanText(customer.mobile)}
                  </Text>
                </View>
              </View>
            </View>
            <View style={styles.singleLineFieldBottom}>
              <Text style={styles.fieldLabel}>Address:</Text>
              <View style={styles.fieldValueLine}>
                <Text style={styles.fieldValueText}>
                  {cleanText(customer.address)}
                </Text>
              </View>
            </View>
          </View>

          <View style={styles.detailTable}>
            <View style={styles.tableHeader}>
              <View style={styles.colDetail}>
                <Text style={styles.th}>Item Name</Text>
              </View>
              <View style={styles.colGrossWeight}>
                <Text style={styles.th}>Gross Weight</Text>
              </View>
              <View style={styles.colWeight}>
                <Text style={styles.th}>Issue</Text>
              </View>
              <View style={styles.colAmount}>
                <Text style={styles.th}>Est. Amount</Text>
              </View>
            </View>

            <View style={styles.bodyArea}>
              <View style={styles.bodyColumns}>
                <View style={styles.detailColBody} />
                <View style={styles.grossWeightColBody} />
                <View style={styles.weightColBody} />
                <View style={styles.amountColBody} />
              </View>

              <View style={styles.watermarkDetailArea}>
                <View style={styles.watermarkWrap}>
                  <Image
                    src={getTenantBrandImageSrcWithFallback("sidebar_icon")}
                    style={styles.watermark}
                  />
                </View>
              </View>

              <View style={styles.rowsLayer}>
                {paddedRows.map((item, idx) => (
                  <View key={`row-${idx}`} style={styles.itemRow} wrap={false}>
                    <View style={styles.itemDetailCell}>
                      <Text style={styles.itemDetail}>
                        {item ? buildDetailText(item) : " "}
                      </Text>
                    </View>
                    <View style={styles.itemGrossWeightCell}>
                      <Text style={styles.itemGrossWeight}>
                        {item ? buildWeightText(item) : " "}
                      </Text>
                    </View>
                    <View style={styles.itemWeightCell}>
                      <Text style={styles.itemWeight}>
                        {item ? buildIssueText(item) : " "}
                      </Text>
                    </View>
                    <View style={styles.itemAmountCell}>
                      <Text style={styles.itemAmount}>
                        {item ? toNumber(item.amount).toFixed(2) : " "}
                      </Text>
                    </View>
                  </View>
                ))}
              </View>
            </View>
          </View>

          <View style={styles.totalRow}>
            <View style={styles.totalDetailCell}>
              <Text style={styles.noteText}>Adv. Paid: {noteText}</Text>
            </View>
            <View style={styles.totalGrossWeightCell} />
            <View style={styles.totalLabelCell}>
              <Text style={styles.totalLabel}>Est. Total</Text>
            </View>
            <View style={styles.totalAmountCell}>
              <Text style={styles.totalAmount}>{totalAmount.toFixed(2)}</Text>
            </View>
          </View>

          <View style={styles.footer}>
            <View style={styles.terms}>
              <Text style={styles.termsText}>
                (૧) ગીલેટ તથા રીપેરીંગ કામ ગ્રાહકના જોખમે રહેશે. (૨) ગીલેટ તથા
                રીપેરીંગ માટે આપેલ વસ્તુ ૧૫ દિવસમાં લઈ જવી.
              </Text>
              <Text style={styles.termsText}>
                (૩) બીલ સાથે લાવવું જરૂરી છે. (૪) રીપેરીંગ કામમાં મેલ પોલીશ ઘટ્ટ
                પડશે.
              </Text>
              <Text style={styles.termsText}>
                (૫) આ પહોંચ સિવાય દાગીનાની ડીલીવરી મળશે નહિ. (૬) ન્યાય ક્ષેત્ર
                અમદાવાદ રહેશે.
              </Text>
            </View>

            <View style={styles.signature}>
              <Text style={{ fontSize: 9 }}>
                For,{" "}
                <Text style={signatureCompanyStyle}>
                  {companyNameUpper}
                </Text>{" "}
              </Text>
            </View>
          </View>
        </View>
      </Page>
    </Document>
  );
};

export default RepairingSlipPDF;
